// Small modal shown when a user pauses a task — captures the reason for the
// hold (one of six canonical categories) and an optional note. The selected
// values flow into adminSetTaskStatus() which records them on
// task_progress_log.hold_reason / hold_notes AND mirrors them onto
// tasks.current_hold_reason for cheap list rendering. See migration 007.

import { useState } from 'react';
import {
    X, Package, Users, FileText, Cloud, Wrench, HelpCircle, Pause,
} from 'lucide-react';
import { HOLD_REASONS, HoldReason } from '../../lib/api';

interface HoldReasonModalProps {
    taskName: string;
    busy?: boolean;
    onCancel: () => void;
    onConfirm: (reason: HoldReason, notes?: string) => void;
}

// Icon + short blurb per reason so the choices read as a quick, scannable
// menu rather than six unlabeled radio buttons.
const REASON_META: Record<HoldReason, { Icon: typeof Package; hint: string }> = {
    Material: { Icon: Package,     hint: 'Material shortage / delivery delay' },
    Manpower: { Icon: Users,       hint: 'Crew unavailable / attendance issue' },
    Drawing:  { Icon: FileText,    hint: 'Drawings / RFI awaited' },
    External: { Icon: Cloud,       hint: 'Weather, permits, 3rd party' },
    Machine:  { Icon: Wrench,      hint: 'Equipment / machine issue' },
    Others:   { Icon: HelpCircle,  hint: 'Other — describe in notes' },
};

export default function HoldReasonModal({
    taskName,
    busy = false,
    onCancel,
    onConfirm,
}: HoldReasonModalProps) {
    const [reason, setReason] = useState<HoldReason | null>(null);
    const [notes, setNotes]   = useState('');

    const canSubmit = reason !== null && !busy;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 z-50"
                onClick={busy ? undefined : onCancel}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md pointer-events-auto">
                    <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                            <Pause size={18} className="text-amber-600 dark:text-amber-400" />
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                Put task on hold
                            </h3>
                        </div>
                        <button
                            onClick={onCancel}
                            disabled={busy}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 disabled:opacity-40"
                        >
                            <X size={18} />
                        </button>
                    </header>

                    <div className="px-5 py-4 space-y-4">
                        <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Task</div>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                {taskName}
                            </div>
                        </div>

                        <div>
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                                Why is this on hold? <span className="text-red-500">*</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {HOLD_REASONS.map((r) => {
                                    const { Icon, hint } = REASON_META[r];
                                    const selected = reason === r;
                                    return (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => setReason(r)}
                                            className={[
                                                'flex items-start gap-2 p-3 rounded-md border text-left transition-colors',
                                                selected
                                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 ring-1 ring-amber-400'
                                                    : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200',
                                            ].join(' ')}
                                        >
                                            <Icon size={16} className={selected ? 'text-amber-600 dark:text-amber-400 mt-0.5' : 'text-gray-400 mt-0.5'} />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium">{r}</div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    {hint}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                Notes <span className="text-gray-400">(optional)</span>
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                maxLength={500}
                                placeholder="Add context — vendor name, expected date, crew, etc."
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                            <div className="text-[11px] text-gray-400 mt-1 text-right">
                                {notes.length}/500
                            </div>
                        </div>
                    </div>

                    <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-slate-700">
                        <button
                            onClick={onCancel}
                            disabled={busy}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-40"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => reason && onConfirm(reason, notes.trim() || undefined)}
                            disabled={!canSubmit}
                            className="px-4 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? 'Saving…' : 'Put on hold'}
                        </button>
                    </footer>
                </div>
            </div>
        </>
    );
}
